(ns arthur.domain.sha256
  "SHA-256, synchronous, in pure ClojureScript.

  WHY NOT `crypto.subtle`. It is async, and every caller here is a pure function
  in the `(f params inputs) -> output` shape: a content address is computed in the
  middle of `flow/freeze`, inside a `let`, and a promise there would turn the
  whole stage inside out. `crypto.createHash` exists in node and not in the
  browser, which is worse — the tests would be hashing with a different
  implementation from the app.

  WHY NOT A DEPENDENCY. It is sixty lines, it never changes, and the thing it has
  to agree with is not another JS library: it is Python's `hashlib`. The server
  recomputes the key of every block and every analysis it is handed and refuses a
  mismatch (see clips/views.py), so a disagreement between the two languages is
  not a hash that looks different — it is an upload that 409s with nothing wrong.
  `sha256-test` therefore pins the digests that `hashlib` produced, including the
  55/56/63/64 and 119/120-byte cases either side of both padding boundaries,
  which is where a hand-written implementation is wrong if it is wrong at all.

  SIGN. JS bitwise operators work on 32-bit SIGNED integers, so `bit-xor` and
  `bit-shift-left` hand back negative numbers, and a negative number entering an
  addition mod 2^32 is off by 2^32. Every intermediate that feeds an addition is
  therefore normalised through `u32`. That is the bug this implementation would
  have, and it is invisible on short inputs — `\"abc\"` passes with the sign bug in
  place on some rounds — which is the other reason the vectors above are pinned."
  (:require [clojure.string :as str]))

(def ^:private round-k
  (js/Uint32Array.
   #js [0x428a2f98 0x71374491 0xb5c0fbcf 0xe9b5dba5 0x3956c25b 0x59f111f1
        0x923f82a4 0xab1c5ed5 0xd807aa98 0x12835b01 0x243185be 0x550c7dc3
        0x72be5d74 0x80deb1fe 0x9bdc06a7 0xc19bf174 0xe49b69c1 0xefbe4786
        0x0fc19dc6 0x240ca1cc 0x2de92c6f 0x4a7484aa 0x5cb0a9dc 0x76f988da
        0x983e5152 0xa831c66d 0xb00327c8 0xbf597fc7 0xc6e00bf3 0xd5a79147
        0x06ca6351 0x14292967 0x27b70a85 0x2e1b2138 0x4d2c6dfc 0x53380d13
        0x650a7354 0x766a0abb 0x81c2c92e 0x92722c85 0xa2bfe8a1 0xa81a664b
        0xc24b8b70 0xc76c51a3 0xd192e819 0xd6990624 0xf40e3585 0x106aa070
        0x19a4c116 0x1e376c08 0x2748774c 0x34b0bcb5 0x391c0cb3 0x4ed8aa4a
        0x5b9cca4f 0x682e6ff3 0x748f82ee 0x78a5636f 0x84c87814 0x8cc70208
        0x90befffa 0xa4506ceb 0xbef9a3f7 0xc67178f2]))

(defn- u32 [x] (unsigned-bit-shift-right x 0))

(defn- rotr [x n]
  (u32 (bit-or (unsigned-bit-shift-right x n) (bit-shift-left x (- 32 n)))))

(defn- pad
  "The message, padded: a 0x80 byte, zeros, and the bit length as a big-endian
  64-bit integer. The length is written as two 32-bit halves because a JS number
  cannot hold a 64-bit integer and nothing here will ever hash 512MB."
  [^js bytes]
  (let [n     (.-length bytes)
        total (* 64 (js/Math.ceil (/ (+ n 9) 64)))
        out   (js/Uint8Array. total)
        bits  (* 8 n)]
    (.set out bytes)
    (aset out n 0x80)
    ;; The high half is the bit count above 2^32; exact for any input JS can hold.
    (let [hi (js/Math.floor (/ bits 4294967296))
          lo (u32 bits)]
      (dotimes [i 4]
        (aset out (+ total -8 i) (bit-and 0xff (unsigned-bit-shift-right hi (* 8 (- 3 i)))))
        (aset out (+ total -4 i) (bit-and 0xff (unsigned-bit-shift-right lo (* 8 (- 3 i)))))))
    out))

(defn digest
  "SHA-256 of a Uint8Array, as a Uint8Array of 32 bytes."
  [^js bytes]
  (let [msg (pad bytes)
        h   (js/Uint32Array. #js [0x6a09e667 0xbb67ae85 0x3c6ef372 0xa54ff53a
                                  0x510e527f 0x9b05688c 0x1f83d9ab 0x5be0cd19])
        w   (js/Uint32Array. 64)
        v   (js/Uint32Array. 8)]
    (dotimes [block (quot (.-length msg) 64)]
      (let [base (* 64 block)]
        (dotimes [i 16]
          (let [o (+ base (* 4 i))]
            (aset w i (u32 (bit-or (bit-shift-left (aget msg o) 24)
                                   (bit-shift-left (aget msg (+ o 1)) 16)
                                   (bit-shift-left (aget msg (+ o 2)) 8)
                                   (aget msg (+ o 3)))))))
        (dotimes [j 48]
          (let [i  (+ j 16)
                x  (aget w (- i 15))
                y  (aget w (- i 2))
                s0 (u32 (bit-xor (rotr x 7) (rotr x 18) (unsigned-bit-shift-right x 3)))
                s1 (u32 (bit-xor (rotr y 17) (rotr y 19) (unsigned-bit-shift-right y 10)))]
            (aset w i (+ (aget w (- i 16)) s0 (aget w (- i 7)) s1))))
        (.set v h)
        (dotimes [i 64]
          (let [a (aget v 0) b (aget v 1) c (aget v 2) d (aget v 3)
                e (aget v 4) f (aget v 5) g (aget v 6) hh (aget v 7)
                s1    (u32 (bit-xor (rotr e 6) (rotr e 11) (rotr e 25)))
                choice (u32 (bit-xor (bit-and e f) (bit-and (bit-not e) g)))
                t1    (+ hh s1 choice (aget round-k i) (aget w i))
                s0    (u32 (bit-xor (rotr a 2) (rotr a 13) (rotr a 22)))
                maj   (u32 (bit-xor (bit-and a b) (bit-and a c) (bit-and b c)))
                t2    (+ s0 maj)]
            (aset v 7 g) (aset v 6 f) (aset v 5 e)
            (aset v 4 (+ d t1))
            (aset v 3 c) (aset v 2 b) (aset v 1 a)
            (aset v 0 (+ t1 t2))))
        (dotimes [i 8]
          (aset h i (+ (aget h i) (aget v i))))))
    (let [out (js/Uint8Array. 32)]
      (dotimes [i 8]
        (dotimes [b 4]
          (aset out (+ (* 4 i) b)
                (bit-and 0xff (unsigned-bit-shift-right (aget h i) (* 8 (- 3 b)))))))
      out)))

(defn hex
  "Lowercase hex of a byte array, which is the form `hashlib.hexdigest()` gives
  and therefore the form a key is written in."
  [^js bytes]
  (str/join (map (fn [i] (.padStart (.toString (aget bytes i) 16) 2 "0"))
                 (range (.-length bytes)))))

(defn of-bytes [^js bytes] (hex (digest bytes)))

(def ^:private utf8 (js/TextEncoder.))

(defn of-string
  "UTF-8 first, and that is not a detail: a descriptor holds source filenames, so
  a clip called \"café.mov\" hashes to what Python's `hashlib` gives for the same
  bytes only if the encoding is agreed. `TextEncoder` is UTF-8 by definition."
  [s]
  (of-bytes (.encode utf8 s)))

(defn key-of
  "The form a tier-2 key is written in everywhere: \"sha256:<64 hex>\".

  PREFIXED, because a bare hex string in a document says nothing about what
  produced it, and the first time this changes algorithm every stored key has to
  be readable as the old one. It is also what makes a descriptive placeholder
  key — the `\"take/geom\"` these replaced — impossible to confuse with an address."
  [s]
  (str "sha256:" (of-string s)))

(defn key?
  "Does this string name a content address?

  A string test and not a lookup, on purpose: tier 1 must be checkable without
  tier 2 in hand, which is the whole point of the split. It is what `domain/leaf`
  uses to refuse a document carrying a placeholder key like the \"take/geom\" that
  content addressing replaced."
  [s]
  (boolean (and (string? s) (re-matches #"sha256:[0-9a-f]{64}" s))))
